import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../../context/AuthContext';
import './Pricing.css';

// Stripe price IDs from your Stripe Dashboard (sandbox)
const STRIPE_PRICES = {
  silver: {
    monthly: 'price_1ThJkR3VHf8WBCOl34IBGMjx',
  },
  gold: {
    monthly: 'price_1ThJkp3VHf8WBCOlduBuxlCZ',
  },
};

// Cloud Function base URL — update after first deploy
const STRIPE_API_BASE =
  'https://us-central1-clipit-8b9bd.cloudfunctions.net/stripeAPI';

const Pricing = () => {
  const navigate = useNavigate();
  const { user, getIdToken } = useAuth();
  const [loadingTier, setLoadingTier] = useState(null);

  const tiers = [
    {
      id: 'tier-silver',
      key: 'silver',
      name: 'Kaylus Silver',
      description: 'Upgrade your cash-back experience.',
      price: '$5.99',
      period: '/mo',
      features: [
        'Access to Silver exclusive offers',
        'Faster receipt processing',
        'Priority customer support',
        'Reduced payout minimums',
      ],
      cta: 'Start Free Trial',
      isPopular: false,
    },
    {
      id: 'tier-gold',
      key: 'gold',
      name: 'Kaylus Gold',
      description: 'The ultimate shopper reward tier.',
      price: '$8.49',
      period: '/mo',
      features: [
        'Everything in Silver',
        'Access to Gold exclusive locked offers',
        'Instant receipt approvals',
        'Cash-back multipliers on select brands',
      ],
      cta: 'Upgrade to Gold',
      isPopular: true,
    },
  ];

  const handleSubscribe = async (tier) => {
    // Must be logged in to subscribe
    if (!user) {
      navigate('/login');
      return;
    }

    const priceId = STRIPE_PRICES[tier.key]?.monthly;
    if (!priceId) {
      console.error('No price ID found for tier:', tier.key);
      return;
    }

    setLoadingTier(tier.key);

    try {
      const token = await getIdToken();

      const res = await fetch(`${STRIPE_API_BASE}/v1/checkout/session`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify({ priceId }),
      });

      const data = await res.json();

      if (!res.ok) {
        throw new Error(data.error || 'Failed to create checkout session');
      }

      // Redirect to Stripe Checkout
      window.location.href = data.url;
    } catch (err) {
      console.error('Checkout error:', err);
      alert(err.message || 'Something went wrong. Please try again.');
      setLoadingTier(null);
    }
  };

  return (
    <div className="pricing-container">
      <div className="pricing-header">
        <h1 className="pricing-title">Kaylus Subscriptions</h1>
        <p className="pricing-subtitle">
          Simple, transparent pricing for Kaylus. No hidden fees. Cancel anytime.
        </p>
      </div>

      <div className="pricing-cards">
        {tiers.map((tier) => (
          <div key={tier.id} className={`pricing-card ${tier.isPopular ? 'popular' : ''}`}>
            {tier.isPopular && <div className="popular-badge">Most Popular</div>}

            <div className="card-header">
              <h3>{tier.name}</h3>
              <p className="tier-desc">{tier.description}</p>
            </div>

            <div className="card-price">
              <span className="price-amount">{tier.price}</span>
              <span className="price-period">{tier.period}</span>
            </div>

            <button
              className={`cta-button ${tier.isPopular ? 'primary' : 'secondary'}`}
              onClick={() => handleSubscribe(tier)}
              disabled={loadingTier === tier.key}
            >
              {loadingTier === tier.key ? 'Redirecting…' : tier.cta}
            </button>

            <ul className="feature-list">
              {tier.features.map((feature, idx) => (
                <li key={idx}>
                  <svg className="check-icon" viewBox="0 0 20 20" fill="currentColor">
                    <path
                      fillRule="evenodd"
                      d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z"
                      clipRule="evenodd"
                    />
                  </svg>
                  {feature}
                </li>
              ))}
            </ul>
          </div>
        ))}
      </div>
    </div>
  );
};

export default Pricing;
